import { ApolloClient, ApolloLink, createHttpLink, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { fetchAuthSession } from 'aws-amplify/auth';
import { getRuntimeConfig } from '@/lib/runtime-config';

const authLink = setContext(async (_, { headers }) => {
  const session = await fetchAuthSession();

  const accessToken = session?.tokens?.accessToken?.toString();

  return {
    headers: {
      ...headers,
      authorization: accessToken ? `Bearer ${accessToken}` : '',
    },
  };
});

const httpLink = createHttpLink({
  uri: getRuntimeConfig().graphqlBackend || process.env.REACT_APP_GRAPHQL_BACKEND,
});

// Add retry link with exponential backoff
const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: Infinity,
    jitter: true,
  },
  attempts: {
    max: 5,
    retryIf: (error, _operation) => {
      // Retry on network errors and 5xx errors
      if (error && 'statusCode' in error && (error as any).statusCode >= 500) return true;
      if (error && error.message && error.message.includes('Failed to fetch')) return true;
      if (error && error.message && error.message.includes('Network request failed')) return true;
      return false;
    },
  },
});

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      console.error(
        `GraphQL error: Message: ${message}, Location: ${locations ? JSON.stringify(locations) : 'N/A'}, Path: ${path}`
      );
      // Only show user-facing errors, not all GraphQL errors
      if (window.__APOLLO_GLOBAL_ERROR_HANDLER__ && !message.includes('Internal server error')) {
        window.__APOLLO_GLOBAL_ERROR_HANDLER__(message, 'error');
      }
    });
  }

  if (networkError) {
    console.error(`Network error: ${networkError.message}`);

    // Handle specific network errors
    if (networkError.message.includes('Failed to fetch')) {
      if (window.__APOLLO_GLOBAL_ERROR_HANDLER__) {
        window.__APOLLO_GLOBAL_ERROR_HANDLER__(
          'Unable to connect to server. Please check your connection and try again.',
          'error'
        );
      }
    } else if ('statusCode' in networkError && (networkError as any).statusCode === 401) {
      // Handle authentication errors
      console.error('Authentication error - redirecting to login');
      // The auth link should handle re-authentication
    } else if ('statusCode' in networkError && (networkError as any).statusCode >= 500) {
      if (window.__APOLLO_GLOBAL_ERROR_HANDLER__) {
        window.__APOLLO_GLOBAL_ERROR_HANDLER__('Server error occurred. Please try again later.', 'error');
      }
    } else if (window.__APOLLO_GLOBAL_ERROR_HANDLER__) {
      window.__APOLLO_GLOBAL_ERROR_HANDLER__(networkError.message, 'error');
    }
  }
});

const link = ApolloLink.from([errorLink, retryLink, authLink, httpLink]);

export const apolloClient = new ApolloClient({
  link: link,
  cache: new InMemoryCache(),
  connectToDevTools: true,
});
